
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import {
  billingCurrencySymbol,
  formatBillingAmount,
} from '@/composables/billing/formatAmount';

export type AutoRechargeFormMode = 'enable' | 'edit';

export interface AutoRechargeFormValue {
  threshold: number;
  rechargeAmount: number;
}

export default defineComponent({
  name: 'AutoRechargeFormDialog',

  components: { Dialog, InputNumber },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    /** `enable` = first activation, `edit` = modify existing config. */
    mode: {
      type: String as PropType<AutoRechargeFormMode>,
      default: 'enable',
    },

    /** Pre-filled values (current config or defaults for first activation). */
    initialThreshold: {
      type: Number as PropType<number | null>,
      default: null,
    },

    initialRechargeAmount: {
      type: Number as PropType<number | null>,
      default: null,
    },

    submitting: {
      type: Boolean,
      default: false,
    },

    errorMessage: {
      type: String as PropType<string | null>,
      default: null,
    },

    /** Shop billing currency — drives the input suffix + amount format. */
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },
  },

  emits: ['update:visible', 'confirm', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const currencySymbol = computed(() => billingCurrencySymbol(props.currency));

    // Local working copy — committed via `confirm` event, dropped on cancel.
    const threshold = ref<number | null>(props.initialThreshold ?? 10);
    const rechargeAmount = ref<number | null>(props.initialRechargeAmount ?? 25);

    // Re-seed when the dialog re-opens with different initial values (e.g.
    // switching from enable-flow to edit-flow without unmounting).
    watch(
      () => props.visible,
      (v) => {
        if (!v) return;
        threshold.value = props.initialThreshold ?? 10;
        rechargeAmount.value = props.initialRechargeAmount ?? 25;
      },
    );

    const isValid = computed(() => (
      !!threshold.value && !!rechargeAmount.value
      && threshold.value > 0 && rechargeAmount.value > 0
    ));

    const onThresholdChange = (v: number | null | undefined) => {
      threshold.value = v == null ? null : Number(v);
    };
    const onAmountChange = (v: number | null | undefined) => {
      rechargeAmount.value = v == null ? null : Number(v);
    };

    const onConfirm = () => {
      if (!isValid.value || props.submitting) return;
      emit('confirm', {
        threshold: threshold.value!,
        rechargeAmount: rechargeAmount.value!,
      } as AutoRechargeFormValue);
    };

    const onCancel = () => {
      if (props.submitting) return;
      emit('cancel');
      emit('update:visible', false);
    };

    const onVisibleChange = (v: boolean) => {
      if (!v && !props.submitting) {
        emit('cancel');
        emit('update:visible', false);
      }
    };

    const formatAmount = (n: number) => formatBillingAmount(n, props.currency);

    return {
      t,
      threshold,
      rechargeAmount,
      isValid,
      currencySymbol,
      onThresholdChange,
      onAmountChange,
      onConfirm,
      onCancel,
      onVisibleChange,
      formatAmount,
    };
  },
});
